import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/ToastProvider';
import { hasFrontendPermission } from '../permissions';

const STATUS_OPTIONS = ['active', 'on_leave', 'unavailable'];

const STATUS_LABELS = {
  active: 'Active',
  on_leave: 'On Leave',
  unavailable: 'Unavailable',
};

const makeDefaultAvailability = () => ({
  monday: { start: '08:00', end: '17:00' },
  tuesday: { start: '08:00', end: '17:00' },
  wednesday: { start: '08:00', end: '17:00' },
  thursday: { start: '08:00', end: '17:00' },
  friday: { start: '08:00', end: '17:00' },
  saturday: null,
  sunday: null,
});

const makeEmptyDraft = () => ({
  name: '',
  email: '',
  phone: '',
  skills: [],
  hourly_rate: 50,
  certifications: [],
  status: 'active',
  color: '#0ea5e9',
  notes: '',
  availability: makeDefaultAvailability(),
});

const formatStatus = (value) => STATUS_LABELS[String(value || '').toLowerCase()] || 'Unknown';

const initialsFor = (value) => {
  const chars = String(value || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
  return chars || '?';
};

const rate = (value) => `$${Number(value || 0).toFixed(0)}/hr`;

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M6.5 2v9M2 6.5h9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

function TeamStat({ label, value, tone = 'default' }) {
  return (
    <article className={`team-stat-card team-stat-card--${tone}`}>
      <span className="team-stat-card-label">{label}</span>
      <strong className="team-stat-card-value">{value}</strong>
    </article>
  );
}

function TeamField({ label, children }) {
  return (
    <label className="team-field">
      <span className="team-field-label">{label}</span>
      {children}
    </label>
  );
}

function TeamListItem({ tech, active, onSelect }) {
  return (
    <button
      type="button"
      className={`team-list-item${active ? ' is-active' : ''}`}
      onClick={() => onSelect(tech.id)}
    >
      <div className="team-list-avatar" style={{ backgroundColor: tech.color || '#0ea5e9' }}>
        {initialsFor(tech.name)}
      </div>
      <div className="team-list-content">
        <strong className="team-list-name">{tech.name || 'Unnamed tech'}</strong>
        <span className="team-list-meta">{tech.email || 'No email'}</span>
        <span className="team-list-meta">{tech.phone || 'No phone'}</span>
      </div>
      <span className={`team-status-badge team-status-badge--${String(tech.status || '').toLowerCase()}`}>
        {formatStatus(tech.status)}
      </span>
    </button>
  );
}

export function TeamPage() {
  const navigate = useNavigate();
  const { pushToast } = useToast();
  const auth = loadStoredAuth();
  const token = auth?.token;
  const user = auth?.user;
  const canManageTeam = hasFrontendPermission(user, 'technicians.manage');
  const canDeleteTeam = hasFrontendPermission(user, 'technicians.delete.any');

  const [technicians, setTechnicians] = useState([]);
  const [skills, setSkills] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTechId, setSelectedTechId] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [draft, setDraft] = useState(makeEmptyDraft);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const [techData, skillsData] = await Promise.all([
        apiFetch('/api/technicians', { token }),
        apiFetch('/api/technicians/skills', { token }).catch(() => []),
      ]);
      setTechnicians(Array.isArray(techData) ? techData : []);
      setSkills(Array.isArray(skillsData) ? skillsData : []);
    } catch (e) {
      setError(e.message || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshKey]);

  const filteredTechnicians = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return technicians;
    return technicians.filter((tech) => (
      [
        tech.name,
        tech.email,
        tech.phone,
        ...(Array.isArray(tech.skills) ? tech.skills : []),
        ...(Array.isArray(tech.certifications) ? tech.certifications : []),
      ].some((value) => String(value || '').toLowerCase().includes(term))
    ));
  }, [technicians, searchTerm]);

  useEffect(() => {
    if (!filteredTechnicians.length) {
      setSelectedTechId('');
      return;
    }
    if (!filteredTechnicians.some((tech) => tech.id === selectedTechId)) {
      setSelectedTechId(filteredTechnicians[0].id);
    }
  }, [filteredTechnicians, selectedTechId]);

  const selectedTech = useMemo(
    () => filteredTechnicians.find((tech) => tech.id === selectedTechId) || null,
    [filteredTechnicians, selectedTechId],
  );

  const stats = useMemo(() => ({
    total: technicians.length,
    active: technicians.filter((tech) => String(tech.status).toLowerCase() === 'active').length,
    onLeave: technicians.filter((tech) => String(tech.status).toLowerCase() === 'on_leave').length,
    unavailable: technicians.filter((tech) => String(tech.status).toLowerCase() === 'unavailable').length,
  }), [technicians]);

  const updateSelectedTechField = (key, value) => {
    setTechnicians((prev) => prev.map((tech) => (
      tech.id === selectedTechId ? { ...tech, [key]: value } : tech
    )));
  };

  const handleRefresh = () => {
    setRefreshKey((value) => value + 1);
  };

  const handleSaveTech = async () => {
    if (!token || !canManageTeam || !selectedTech) return;
    setSaving(true);
    setError('');
    try {
      const updated = await apiFetch(`/api/technicians/${encodeURIComponent(selectedTech.id)}`, {
        token,
        method: 'PUT',
        body: {
          name: selectedTech.name,
          email: selectedTech.email || '',
          phone: selectedTech.phone || '',
          skills: selectedTech.skills || [],
          hourly_rate: Number(selectedTech.hourly_rate || 0),
          certifications: selectedTech.certifications || [],
          status: selectedTech.status || 'active',
          color: selectedTech.color || '#0ea5e9',
          notes: selectedTech.notes || '',
          availability: selectedTech.availability || makeDefaultAvailability(),
        },
      });
      setTechnicians((prev) => prev.map((tech) => (tech.id === updated.id ? updated : tech)));
      pushToast(`Saved ${updated.name}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to save technician');
      pushToast(e.message || 'Failed to save technician', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTech = async (event) => {
    event.preventDefault();
    if (!token || !canManageTeam) return;
    setSaving(true);
    setError('');
    try {
      const created = await apiFetch('/api/technicians', {
        token,
        method: 'POST',
        body: draft,
      });
      setTechnicians((prev) => [...prev, created]);
      setSelectedTechId(created.id);
      setDraft(makeEmptyDraft());
      setShowCreateForm(false);
      pushToast(`Created ${created.name}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to create technician');
      pushToast(e.message || 'Failed to create technician', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTech = async () => {
    if (!token || !canDeleteTeam || !selectedTech) return;
    if (!window.confirm(`Delete ${selectedTech.name}?`)) return;
    setSaving(true);
    setError('');
    try {
      await apiFetch(`/api/technicians/${encodeURIComponent(selectedTech.id)}`, {
        token,
        method: 'DELETE',
      });
      setTechnicians((prev) => prev.filter((tech) => tech.id !== selectedTech.id));
      pushToast(`Deleted ${selectedTech.name}`, 'success');
    } catch (e) {
      setError(e.message || 'Failed to delete technician');
      pushToast(e.message || 'Failed to delete technician', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <section className="team-page-shell">
        <Skeleton lines={10} />
      </section>
    );
  }

  return (
    <section className="team-page-shell">
      <div className="team-page-header">
        <div>
          <span className="team-page-eyebrow">People</span>
          <h1 className="team-page-heading">Team</h1>
          <p className="team-page-subheading">
            Technician roster, skills inventory, capacity context, and individual profile details for field staffing.
          </p>
        </div>
        <div className="team-page-header-actions">
          <Button variant="secondary" onClick={handleRefresh}>
            <RefreshIcon />
            Refresh
          </Button>
          {canManageTeam ? (
            <Button onClick={() => setShowCreateForm((value) => !value)}>
              <PlusIcon />
              New Technician
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <ErrorState message={error} onRetry={loadData} /> : null}

      <div className="team-page-stats-grid">
        <TeamStat label="Total" value={stats.total} />
        <TeamStat label="Active" value={stats.active} tone="accent" />
        <TeamStat label="On Leave" value={stats.onLeave} tone="warning" />
        <TeamStat label="Unavailable" value={stats.unavailable} tone="danger" />
      </div>

      {showCreateForm ? (
        <Card className="team-create-card" title="Create Technician">
          <form className="team-form-grid" onSubmit={handleCreateTech}>
            <TeamField label="Name">
              <input className="team-input" value={draft.name} onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))} placeholder="Jordan Alvarez" required />
            </TeamField>
            <TeamField label="Email">
              <input className="team-input" type="email" value={draft.email} onChange={(event) => setDraft((prev) => ({ ...prev, email: event.target.value }))} placeholder="jordan@fieldops.com" />
            </TeamField>
            <TeamField label="Phone">
              <input className="team-input" value={draft.phone} onChange={(event) => setDraft((prev) => ({ ...prev, phone: event.target.value }))} placeholder="(555) 123-4567" />
            </TeamField>
            <TeamField label="Hourly Rate">
              <input className="team-input" type="number" min="0" step="1" value={draft.hourly_rate} onChange={(event) => setDraft((prev) => ({ ...prev, hourly_rate: Number(event.target.value || 0) }))} />
            </TeamField>
            <TeamField label="Status">
              <select className="team-input" value={draft.status} onChange={(event) => setDraft((prev) => ({ ...prev, status: event.target.value }))}>
                {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{STATUS_LABELS[option]}</option>)}
              </select>
            </TeamField>
            <TeamField label="Color">
              <input className="team-input team-input--color" type="color" value={draft.color} onChange={(event) => setDraft((prev) => ({ ...prev, color: event.target.value }))} />
            </TeamField>
            <TeamField label="Skills">
              <input className="team-input" value={draft.skills.join(', ')} onChange={(event) => setDraft((prev) => ({ ...prev, skills: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) }))} placeholder="HVAC, Electrical, Plumbing" />
            </TeamField>
            <TeamField label="Certifications">
              <input className="team-input" value={draft.certifications.join(', ')} onChange={(event) => setDraft((prev) => ({ ...prev, certifications: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) }))} placeholder="EPA 608, OSHA 10" />
            </TeamField>
            <TeamField label="Notes">
              <textarea className="team-input team-textarea" value={draft.notes} onChange={(event) => setDraft((prev) => ({ ...prev, notes: event.target.value }))} placeholder="Strengths, region, shift preference" />
            </TeamField>
            <div className="team-form-actions">
              <Button variant="ghost" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              <Button type="submit" disabled={saving}>Create Technician</Button>
            </div>
          </form>
        </Card>
      ) : null}

      <div className="team-page-layout">
        <Card className="team-list-card" title="Roster">
          <div className="team-list-toolbar">
            <input className="team-input" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="Search by name, email, phone, skills, or certs" />
            {skills.length ? (
              <div className="team-skills-cloud">
                {skills.slice(0, 10).map((skill) => (
                  <span className="team-skill-chip" key={skill}>{skill}</span>
                ))}
              </div>
            ) : null}
          </div>

          {filteredTechnicians.length ? (
            <div className="team-list">
              {filteredTechnicians.map((tech) => (
                <TeamListItem key={tech.id} tech={tech} active={tech.id === selectedTechId} onSelect={setSelectedTechId} />
              ))}
            </div>
          ) : (
            <EmptyState title="No technicians in this view" message="Adjust the search or create a new technician profile." />
          )}
        </Card>

        <Card className="team-detail-card" title={selectedTech ? selectedTech.name : 'Details'}>
          {!selectedTech ? (
            <EmptyState title="No technician selected" message="Select a technician from the roster to inspect profile details." />
          ) : (
            <div className="team-detail">
              <div className="team-detail-hero">
                <div className="team-detail-avatar" style={{ backgroundColor: selectedTech.color || '#0ea5e9' }}>
                  {initialsFor(selectedTech.name)}
                </div>
                <div>
                  <div className="team-detail-id">{selectedTech.id}</div>
                  <h2 className="team-detail-name">{selectedTech.name || 'Unnamed technician'}</h2>
                  <p className="team-detail-copy">
                    Skills, certifications, status, and account details stay centralized here for staffing and dispatch decisions.
                  </p>
                </div>
                <span className={`team-status-badge team-status-badge--${String(selectedTech.status || '').toLowerCase()}`}>
                  {formatStatus(selectedTech.status)}
                </span>
              </div>

              <div className="team-detail-grid">
                <TeamField label="Name">
                  {canManageTeam ? (
                    <input className="team-input" value={selectedTech.name || ''} onChange={(event) => updateSelectedTechField('name', event.target.value)} />
                  ) : <div className="team-readout">{selectedTech.name || 'No name'}</div>}
                </TeamField>
                <TeamField label="Email">
                  {canManageTeam ? (
                    <input className="team-input" type="email" value={selectedTech.email || ''} onChange={(event) => updateSelectedTechField('email', event.target.value)} />
                  ) : <div className="team-readout">{selectedTech.email || 'No email'}</div>}
                </TeamField>
                <TeamField label="Phone">
                  {canManageTeam ? (
                    <input className="team-input" value={selectedTech.phone || ''} onChange={(event) => updateSelectedTechField('phone', event.target.value)} />
                  ) : <div className="team-readout">{selectedTech.phone || 'No phone'}</div>}
                </TeamField>
                <TeamField label="Hourly Rate">
                  {canManageTeam ? (
                    <input className="team-input" type="number" min="0" step="1" value={selectedTech.hourly_rate || 0} onChange={(event) => updateSelectedTechField('hourly_rate', Number(event.target.value || 0))} />
                  ) : <div className="team-readout">{rate(selectedTech.hourly_rate)}</div>}
                </TeamField>
                <TeamField label="Status">
                  {canManageTeam ? (
                    <select className="team-input" value={selectedTech.status || 'active'} onChange={(event) => updateSelectedTechField('status', event.target.value)}>
                      {STATUS_OPTIONS.map((option) => <option key={option} value={option}>{STATUS_LABELS[option]}</option>)}
                    </select>
                  ) : <div className="team-readout">{formatStatus(selectedTech.status)}</div>}
                </TeamField>
                <TeamField label="Color">
                  {canManageTeam ? (
                    <input className="team-input team-input--color" type="color" value={selectedTech.color || '#0ea5e9'} onChange={(event) => updateSelectedTechField('color', event.target.value)} />
                  ) : <div className="team-readout">{selectedTech.color || '#0ea5e9'}</div>}
                </TeamField>
                <TeamField label="Skills">
                  {canManageTeam ? (
                    <input className="team-input" value={(selectedTech.skills || []).join(', ')} onChange={(event) => updateSelectedTechField('skills', event.target.value.split(',').map((item) => item.trim()).filter(Boolean))} />
                  ) : <div className="team-readout">{(selectedTech.skills || []).join(', ') || 'No skills'}</div>}
                </TeamField>
                <TeamField label="Certifications">
                  {canManageTeam ? (
                    <input className="team-input" value={(selectedTech.certifications || []).join(', ')} onChange={(event) => updateSelectedTechField('certifications', event.target.value.split(',').map((item) => item.trim()).filter(Boolean))} />
                  ) : <div className="team-readout">{(selectedTech.certifications || []).join(', ') || 'No certs'}</div>}
                </TeamField>
              </div>

              <TeamField label="Notes">
                {canManageTeam ? (
                  <textarea className="team-input team-textarea" value={selectedTech.notes || ''} onChange={(event) => updateSelectedTechField('notes', event.target.value)} />
                ) : <div className="team-readout team-readout--notes">{selectedTech.notes || 'No notes'}</div>}
              </TeamField>

              <div className="team-detail-actions">
                {canManageTeam ? (
                  <Button onClick={handleSaveTech} disabled={saving}>Save Changes</Button>
                ) : null}
                {canDeleteTeam ? (
                  <Button variant="danger" onClick={handleDeleteTech} disabled={saving}>Delete Technician</Button>
                ) : null}
              </div>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
}
